import axios from 'axios';
import { useEffect, useState } from 'react';
import { Space, Typography, theme } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  ToolOutlined,
  FlagOutlined,
} from '@ant-design/icons';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';
import { KPI_GROUPS } from 'assets/data/estadosPresupuesto';

const { useToken } = theme;
const { Text } = Typography;

const ICON_BY_KEY = {
  pending: ClockCircleOutlined,
  approved: CheckCircleOutlined,
  in_progress: ToolOutlined,
  done: FlagOutlined,
};

const TINT_BY_KEY = (token) => ({
  pending: token.colorWarning,
  approved: token.colorInfo,
  in_progress: token.colorPrimary,
  done: token.colorSuccess,
});

function Tile({ group, count, active, tint, icon: Icon, onClick }) {
  const { token } = useToken();
  return (
    <button
      type="button"
      onClick={() => onClick(group)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: token.paddingMD,
        minWidth: 180,
        flex: '1 1 180px',
        textAlign: 'left',
        cursor: 'pointer',
        border: `1px solid ${active ? tint : token.colorBorder}`,
        borderRadius: token.borderRadiusLG,
        background: active ? tint + '14' : token.colorBgContainer,
        boxShadow: active ? `0 0 0 2px ${tint}33` : 'none',
        transition: 'all 0.15s ease',
      }}
    >
      <Space>
        <Icon style={{ color: tint, fontSize: 18 }} />
        <Text type="secondary" style={{ fontSize: 13 }}>
          {group.label}
        </Text>
      </Space>
      <Text strong style={{ fontSize: 26, color: tint, lineHeight: 1 }}>
        {count}
      </Text>
    </button>
  );
}

function BudgetKpiStrip({ activeStages, onFilter, refreshKey = 0 }) {
  const { token } = useToken();
  const [counts, setCounts] = useState({});
  const tints = TINT_BY_KEY(token);

  useEffect(() => {
    let cancelled = false;
    const loadCounts = async () => {
      const apiToken = getAccessTokenApi();
      const results = await Promise.all(
        KPI_GROUPS.map(async (g) => {
          try {
            const { data } = await axios.get(`${API_PATH}/get-budget`, {
              params: { limit: 1, stage: g.stages.join(',') },
              headers: { Authorization: apiToken },
            });
            return [g.key, data.total ?? 0];
          } catch (_err) {
            return [g.key, 0];
          }
        })
      );
      if (!cancelled) {
        setCounts(Object.fromEntries(results));
      }
    };
    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const activeSet = new Set(activeStages ?? []);
  const isActive = (group) =>
    group.stages.length > 0 && group.stages.every((s) => activeSet.has(s));

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: token.marginSM }}>
      {KPI_GROUPS.map((g) => (
        <Tile
          key={g.key}
          group={g}
          count={counts[g.key] ?? 0}
          active={isActive(g)}
          tint={tints[g.key]}
          icon={ICON_BY_KEY[g.key]}
          onClick={onFilter}
        />
      ))}
    </div>
  );
}

export default BudgetKpiStrip;
